import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Preferred MIME types in order. The first supported one wins. This matters
 * because iOS Safari records in mp4, Chrome/Firefox in webm. Never assume.
 */
const CANDIDATES = [
  "video/mp4;codecs=h264,aac",
  "video/mp4;codecs=avc1,mp4a",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

export function pickRecorderMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const t of CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(t)) return t;
    } catch {}
  }
  // Fallback: let MediaRecorder pick its default
  return "";
}

export function extensionFromMime(mime) {
  if (!mime) return "webm";
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("webm")) return "webm";
  return "webm";
}

/**
 * Media recorder lifecycle. Accepts a MediaStream (video + optional audio).
 * Produces a Blob on stop. Also tracks elapsed time in seconds.
 *
 * State machine: "idle" | "recording" | "stopping" | "unsupported" | "error"
 */
export default function useMediaRecorder() {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const [blob, setBlob] = useState(null);
  const [mime, setMime] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);

  const supported = typeof MediaRecorder !== "undefined";

  const start = useCallback(async (stream) => {
    if (!stream) {
      setError("Sem stream disponível.");
      setState("error");
      return;
    }
    if (!supported) {
      setState("unsupported");
      return;
    }
    const chosen = pickRecorderMime();
    if (chosen === null) {
      setState("unsupported");
      return;
    }
    try {
      chunksRef.current = [];
      setBlob(null);
      setError("");
      const opts = chosen ? { mimeType: chosen } : {};
      const rec = new MediaRecorder(stream, opts);
      recorderRef.current = rec;
      setMime(chosen || rec.mimeType || "");
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const effectiveMime = rec.mimeType || chosen || "video/webm";
        const b = new Blob(chunksRef.current, { type: effectiveMime });
        setBlob(b);
        setMime(effectiveMime);
        chunksRef.current = [];
        setState("idle");
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
      rec.onerror = (ev) => {
        setError(ev?.error?.message || "Erro na gravação.");
        setState("error");
      };
      rec.start(1000); // chunk every second
      startedAtRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 500);
      setState("recording");
    } catch (err) {
      setError(err?.message || "Falha ao iniciar a gravação.");
      setState("error");
    }
  }, [supported]);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec) return;
    try {
      if (rec.state !== "inactive") {
        setState("stopping");
        rec.stop();
      }
    } catch {}
  }, []);

  const reset = useCallback(() => {
    setBlob(null);
    setElapsed(0);
    setError("");
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const rec = recorderRef.current;
    if (rec && rec.state === "recording") {
      try { rec.stop(); } catch {}
    }
  }, []);

  return { supported, state, elapsed, error, blob, mime, start, stop, reset };
}
